import { useEffect, useRef, useState } from "react";

const IcebergDiagram = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [counts, setCounts] = useState({ a: 0, v: 0, e: 0 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Staggered animation phases
  useEffect(() => {
    if (isVisible) {
      const phases = [0, 1, 2, 3];
      phases.forEach((phase, index) => {
        setTimeout(() => setAnimationPhase(phase + 1), index * 400);
      });
    }
  }, [isVisible]);

  // Count-up animation for percentages
  useEffect(() => {
    if (animationPhase >= 1) {
      const duration = 1500;
      const steps = 30;
      const interval = duration / steps;
      
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        setCounts({
          a: Math.round(20 * easeOut),
          v: Math.round(30 * easeOut),
          e: Math.round(50 * easeOut),
        });
        
        if (step >= steps) clearInterval(timer);
      }, interval);
      
      return () => clearInterval(timer);
    }
  }, [animationPhase]);

  return (
    <div ref={containerRef} className="relative w-full max-w-4xl mx-auto py-16">
      {/* Background depth gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-sky-900/10 to-navy/30 rounded-3xl pointer-events-none" />
      
      {/* Water line with animated waves */}
      <div className="absolute left-0 right-0 top-[22%] z-10">
        {/* Main water line */}
        <div className="h-1 bg-gradient-to-r from-transparent via-sky-400/60 to-transparent relative overflow-hidden">
          {/* Animated shimmer */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_3s_ease-in-out_infinite]" />
        </div>
        
        {/* Animated waves */}
        <div className="relative h-4 overflow-hidden">
          <svg className="absolute w-[200%] h-full animate-[wave_4s_linear_infinite]" viewBox="0 0 1200 20" preserveAspectRatio="none">
            <path 
              d="M0,10 C150,20 300,0 450,10 C600,20 750,0 900,10 C1050,20 1200,0 1200,10 L1200,20 L0,20 Z" 
              fill="url(#waveGradient)" 
              opacity="0.3"
            />
            <defs>
              <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgb(56, 189, 248)" stopOpacity="0" />
                <stop offset="50%" stopColor="rgb(56, 189, 248)" stopOpacity="0.5" />
                <stop offset="100%" stopColor="rgb(56, 189, 248)" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
          <svg className="absolute w-[200%] h-full animate-[wave_3s_linear_infinite_reverse]" style={{ animationDelay: '-1.5s' }} viewBox="0 0 1200 20" preserveAspectRatio="none">
            <path 
              d="M0,10 C150,0 300,20 450,10 C600,0 750,20 900,10 C1050,0 1200,20 1200,10 L1200,20 L0,20 Z" 
              fill="rgb(56, 189, 248)" 
              opacity="0.2"
            />
          </svg>
        </div>
      </div>
      
      {/* Water line label */}
      <div className={`absolute left-4 md:left-8 top-[22%] -translate-y-1/2 transition-all duration-700 ${
        animationPhase >= 1 ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
      }`}>
        <span className="text-sky-300 text-sm font-medium px-3 py-1 bg-sky-500/10 rounded-full border border-sky-400/30">
          קו המים
        </span>
      </div>

      <div className="relative flex flex-col items-center">
        {/* Top section - Above water (20%) - Attraction */}
        <div 
          className={`relative w-36 md:w-44 transition-all duration-1000 ${
            animationPhase >= 1 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-12"
          }`}
        >
          {/* Sunlight effect above water */}
          <div className="absolute -inset-4 bg-gradient-to-b from-amber-300/20 to-transparent rounded-full blur-xl animate-pulse" />
          
          <div className="relative bg-gradient-to-b from-amber-300/95 to-amber-400/85 rounded-t-3xl pt-6 pb-5 px-4 text-center border-2 border-amber-300/60 shadow-lg shadow-amber-400/30 backdrop-blur-sm">
            <span className="text-navy font-bold text-xl md:text-2xl drop-shadow-sm">A</span>
            <p className="text-navy/90 text-sm md:text-base font-semibold mt-1">Attraction</p>
            <p className="text-navy/70 text-xs md:text-sm mt-1">משיכה והעדפות</p>
          </div>
          
          {/* Percentage badge with count-up */}
          <div className={`absolute -right-14 md:-right-20 top-1/2 -translate-y-1/2 transition-all duration-500 ${
            animationPhase >= 2 ? "opacity-100 scale-100" : "opacity-0 scale-50"
          }`}>
            <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-navy font-bold px-4 py-1.5 rounded-full text-sm shadow-lg shadow-amber-500/30 animate-[subtle-bounce_2s_ease-in-out_infinite]">
              {counts.a}%
            </div>
          </div>
          
          {/* Description */}
          <div className={`absolute -left-4 md:-left-52 top-1/2 -translate-y-1/2 text-right max-w-[180px] hidden md:block transition-all duration-700 ${
            animationPhase >= 3 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
          }`}>
            <p className="text-amber-300/90 text-sm leading-relaxed font-medium">
              זה מה שאפליקציות דייטינג מתמקדות בו
            </p>
            <div className="mt-2 h-px bg-gradient-to-l from-amber-400/50 to-transparent" />
          </div>
        </div>

        {/* Middle section - Below water (30%) - Values */}
        <div 
          className={`relative w-52 md:w-64 transition-all duration-1000 ${
            animationPhase >= 2 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"
          }`}
          style={{ transitionDelay: "200ms" }}
        >
          {/* Underwater effect */}
          <div className="absolute inset-0 bg-sky-500/5 backdrop-blur-[1px] rounded-lg" />
          
          <div className="relative bg-gradient-to-b from-sky-500/85 to-sky-600/85 py-7 px-4 text-center border-x-2 border-sky-400/40 shadow-inner">
            <span className="text-white font-bold text-xl md:text-2xl drop-shadow-md">V</span>
            <p className="text-white/95 text-sm md:text-base font-semibold mt-1">Values</p>
            <p className="text-white/80 text-xs md:text-sm mt-1">ערכים ומטרות</p>
          </div>
          
          {/* Percentage badge */}
          <div className={`absolute -right-14 md:-right-20 top-1/2 -translate-y-1/2 transition-all duration-500 ${
            animationPhase >= 2 ? "opacity-100 scale-100" : "opacity-0 scale-50"
          }`} style={{ transitionDelay: "300ms" }}>
            <div className="bg-gradient-to-r from-sky-400 to-sky-500 text-navy font-bold px-4 py-1.5 rounded-full text-sm shadow-lg shadow-sky-500/30">
              {counts.v}%
            </div>
          </div>
          
          {/* Description */}
          <div className={`absolute -left-4 md:-left-52 top-1/2 -translate-y-1/2 text-right max-w-[180px] hidden md:block transition-all duration-700 ${
            animationPhase >= 3 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
          }`} style={{ transitionDelay: "200ms" }}>
            <p className="text-sky-300/90 text-sm leading-relaxed font-medium">
              התאמה לסגנון חיים
            </p>
            <div className="mt-2 h-px bg-gradient-to-l from-sky-400/50 to-transparent" />
          </div>
          
          {/* Bubbles effect */}
          <div className="absolute -right-2 top-1/4 w-2 h-2 bg-white/20 rounded-full animate-[float-up_3s_ease-in-out_infinite]" />
          <div className="absolute -left-3 top-1/2 w-1.5 h-1.5 bg-white/15 rounded-full animate-[float-up_4s_ease-in-out_infinite_0.5s]" />
        </div>

        {/* Deep section - Base (50%) - Emotional Core */}
        <div 
          className={`relative w-72 md:w-96 transition-all duration-1000 ${
            animationPhase >= 3 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"
          }`}
          style={{ transitionDelay: "400ms" }}
        >
          {/* Deep water overlay */}
          <div className="absolute inset-0 bg-navy/20 backdrop-blur-[2px] rounded-b-[4rem]" />
          
          {/* Pulsing glow effect */}
          <div className="absolute -inset-3 bg-gold/10 rounded-[5rem] blur-2xl animate-[depth-glow_3s_ease-in-out_infinite]" />
          
          <div className="relative bg-gradient-to-b from-navy-dark/95 to-navy/98 rounded-b-[4rem] py-10 px-6 text-center border-2 border-t-0 border-gold/20 shadow-2xl shadow-navy/60">
            {/* Inner glow */}
            <div className="absolute inset-4 bg-gradient-to-t from-gold/5 to-transparent rounded-b-[3rem] pointer-events-none" />
            
            <span className="text-gold font-bold text-2xl md:text-3xl drop-shadow-lg relative">E</span>
            <p className="text-gold/95 text-base md:text-lg font-semibold mt-2 relative">Emotional Core</p>
            <p className="text-cream/80 text-sm md:text-base mt-1 relative">ליבה רגשית</p>
            <p className="text-cream/60 text-xs md:text-sm mt-3 max-w-[220px] mx-auto relative">
              מערכת ההפעלה - דפוסי התקשרות וצרכים
            </p>
            
            {/* Animated highlight line */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-gold/50 to-transparent animate-[shimmer_2s_ease-in-out_infinite]" />
          </div>
          
          {/* Percentage badge */}
          <div className={`absolute -right-14 md:-right-20 top-1/3 -translate-y-1/2 transition-all duration-500 ${
            animationPhase >= 3 ? "opacity-100 scale-100" : "opacity-0 scale-50"
          }`} style={{ transitionDelay: "500ms" }}>
            <div className="bg-gradient-to-r from-gold to-amber-500 text-navy font-bold px-4 py-2 rounded-full text-base shadow-lg shadow-gold/40 animate-[depth-glow_3s_ease-in-out_infinite]">
              {counts.e}%
            </div>
          </div>
          
          {/* Deep bubbles */}
          <div className="absolute -right-4 top-1/3 w-1 h-1 bg-white/10 rounded-full animate-[float-up_5s_ease-in-out_infinite]" />
          <div className="absolute -left-2 top-2/3 w-1.5 h-1.5 bg-white/10 rounded-full animate-[float-up_6s_ease-in-out_infinite_1s]" />
          <div className="absolute right-8 bottom-1/4 w-1 h-1 bg-gold/20 rounded-full animate-[float-up_4s_ease-in-out_infinite_2s]" />
        </div>
      </div>

      {/* Mobile descriptions */}
      <div className={`md:hidden mt-10 space-y-4 text-center text-sm transition-all duration-700 ${
        animationPhase >= 4 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}>
        <div className="bg-amber-400/10 rounded-lg p-3 border border-amber-400/20">
          <p className="text-amber-300">
            <span className="font-bold text-lg">A</span> - זה מה שאפליקציות דייטינג מתמקדות בו
          </p>
        </div>
        <div className="bg-sky-400/10 rounded-lg p-3 border border-sky-400/20">
          <p className="text-sky-300">
            <span className="font-bold text-lg">V</span> - התאמה לסגנון חיים
          </p>
        </div>
        <div className="bg-gold/10 rounded-lg p-3 border border-gold/20">
          <p className="text-gold">
            <span className="font-bold text-lg">E</span> - מערכת ההפעלה - דפוסי התקשרות וצרכים
          </p>
        </div>
      </div>
      
      {/* Depth indicator */}
      <div className={`absolute right-2 md:right-4 top-[30%] bottom-[10%] w-px transition-all duration-1000 ${
        animationPhase >= 4 ? "opacity-100" : "opacity-0"
      }`}>
        <div className="h-full bg-gradient-to-b from-sky-400/30 via-sky-500/50 to-gold/30 rounded-full" />
        <div className="absolute top-0 -left-1 w-3 h-px bg-sky-400/50" />
        <div className="absolute bottom-0 -left-1 w-3 h-px bg-gold/50" />
      </div>
    </div>
  );
};

export default IcebergDiagram;
